/**
 * Shared scenario workbench for vulnerable-vs-CSK theorem demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import type { DemoConfig, DemoMode, DemoResult } from "../types.js";
import { CodeBlock, type CodeLanguage } from "./code/CodeBlock.js";
import { DemoContainer } from "./DemoContainer.js";
import { DemoStage, type DemoStageProps } from "./DemoStage.js";
import { ExerciseGuide } from "./DemoStage.js";
import { ModeToggle } from "./ModeToggle.js";
import { NeutralizationBurst } from "./NeutralizationBurst.js";
import { ResultPanel } from "./ResultPanel.js";
import { useDemoMode, useDemoResult } from "../hooks/index.js";
import type { CanonicalTheoremName } from "../design-system/tokens.js";

export interface DemoScenarioState {
  readonly mode: DemoMode;
  readonly isCSK: boolean;
  readonly result: DemoResult | null;
  readonly setMode: (mode: DemoMode) => void;
  readonly setResult: (result: DemoResult) => void;
  readonly clearResult: () => void;
}

export interface DemoScenarioCode {
  readonly code: string;
  readonly title: string;
  readonly language?: CodeLanguage;
}

export interface DemoScenarioWorkbenchProps {
  readonly config: DemoConfig;
  readonly theorem: CanonicalTheoremName;
  readonly stage: (state: DemoScenarioState) => Omit<DemoStageProps, "children">;
  readonly children: (state: DemoScenarioState) => React.ReactNode;
  readonly afterResult?: (state: DemoScenarioState) => React.ReactNode;
  readonly beforeStage?: (state: DemoScenarioState) => React.ReactNode;
  readonly code?: (state: DemoScenarioState) => DemoScenarioCode;
  readonly exerciseSteps?: readonly React.ReactNode[];
  readonly onModeChange?: (mode: DemoMode) => void;
  readonly witness?: (state: DemoScenarioState) => React.ReactNode;
}

export function DemoScenarioWorkbench({
  config,
  theorem,
  stage,
  children,
  afterResult,
  beforeStage,
  code,
  exerciseSteps,
  onModeChange,
  witness,
}: DemoScenarioWorkbenchProps): React.JSX.Element {
  const { mode, isCSK, setMode } = useDemoMode();
  const { result, setResult, clearResult } = useDemoResult();
  const [burstSeq, setBurstSeq] = React.useState(0);
  const onModeChangeRef = React.useRef(onModeChange);

  React.useEffect(() => {
    onModeChangeRef.current = onModeChange;
  }, [onModeChange]);

  React.useEffect(() => {
    clearResult();
    onModeChangeRef.current?.(mode);
  }, [mode, clearResult]);

  React.useEffect(() => {
    if (result?.type === "neutralized") setBurstSeq((seq) => seq + 1);
  }, [result]);

  const state = React.useMemo<DemoScenarioState>(
    () => ({ clearResult, isCSK, mode, result, setMode, setResult }),
    [clearResult, isCSK, mode, result, setMode, setResult],
  );
  const codeBlock = code?.(state);

  return (
    <DemoContainer config={config}>
      <ModeToggle mode={mode} onChange={setMode} />
      {beforeStage?.(state)}
      <DemoStage {...stage(state)}>
        {children(state)}
        {codeBlock ? (
          <CodeBlock
            code={codeBlock.code}
            language={codeBlock.language ?? "typescript"}
            title={codeBlock.title}
          />
        ) : null}
      </DemoStage>
      <NeutralizationBurst
        active={result?.type === "neutralized"}
        triggerKey={burstSeq}
        theorem={theorem}
      >
        <ResultPanel result={result} />
      </NeutralizationBurst>
      {afterResult?.(state)}
      {witness?.(state)}
      {exerciseSteps ? <ExerciseGuide steps={exerciseSteps} /> : null}
    </DemoContainer>
  );
}
